import { handleApiError } from '../utils/errorHandling';

interface UploadResponse {
  fileUrl: string;
  fileName: string;
  originalName: string;
  size: number;
  mimetype: string;
}

interface UploadOptions {
  applicationToken?: string;
}

export const uploadFile = async (file: File, options: UploadOptions = {}): Promise<UploadResponse> => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    // Determine the endpoint based on whether we're using an application token
    const endpoint = options.applicationToken 
      ? `/api/upload/application/${options.applicationToken}/files`
      : '/api/upload/files';

    console.log('Uploading file to:', {
      endpoint,
      fileName: file.name,
      size: file.size,
      type: file.type
    });

    const headers: HeadersInit = {
      'Accept': '*/*',
      // Don't set Content-Type for FormData, browser will set it with boundary
    };
    
    // For admin uploads, include the JWT token if available
    if (!options.applicationToken) {
      const token = localStorage.getItem('token');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }
    
    const requestInit: RequestInit = {
      method: 'POST',
      body: formData,
      // Always include credentials for proper authentication
      credentials: 'include',
      headers
    };

    try {
      const response = await fetch(endpoint, requestInit);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        console.error('Upload failed:', {
          status: response.status,
          statusText: response.statusText,
          error: errorData
        });
        throw new Error(errorData.message || errorData.error || `Upload failed: ${response.statusText}`);
      }

      const responseData = await response.json();
      console.log('Upload successful:', {
        fileName: responseData.fileName,
        size: responseData.size,
        type: responseData.mimetype
      });

      return responseData;
    } catch (fetchError) {
      console.error('Network error during upload:', fetchError);
      throw new Error('Network error during file upload. Please try again.');
    }
  } catch (error: unknown) {
    console.error('Upload error:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('An unknown error occurred during upload');
  }
};

export const deleteFile = async (fileKey: string, applicationToken?: string): Promise<void> => {
  try {
    // Determine the endpoint based on whether we're using an application token
    const endpoint = applicationToken 
      ? `/api/upload/application/${applicationToken}/files/${encodeURIComponent(fileKey)}`
      : `/api/upload/files/${encodeURIComponent(fileKey)}`;

    const headers: HeadersInit = {};
    
    // For admin uploads, include the JWT token if available
    if (!applicationToken) {
      const token = localStorage.getItem('token');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    const response = await fetch(endpoint, {
      method: 'DELETE',
      credentials: applicationToken ? 'omit' : 'include',
      headers
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(errorData.message || `Delete failed: ${response.statusText}`);
    }
  } catch (error) {
    throw handleApiError(error);
  }
};

export const refreshFileUrl = async (fileKey: string, applicationToken?: string): Promise<string> => {
  try {
    // For application uploads, the token must be in the URL
    const endpoint = applicationToken
      ? `/api/upload/application/${applicationToken}/files/${encodeURIComponent(fileKey)}`
      : `/api/upload/files/${encodeURIComponent(fileKey)}`;

    console.log('Refreshing file URL:', {
      endpoint,
      fileKey
    });

    const headers: HeadersInit = {
      'Accept': '*/*'
    };
    
    // For admin uploads, include the JWT token if available
    if (!applicationToken) {
      const token = localStorage.getItem('token');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }
    
    const requestInit: RequestInit = {
      method: 'GET',
      // Only include credentials for admin uploads
      credentials: applicationToken ? 'omit' : 'include',
      headers
    };

    try {
      const response = await fetch(endpoint, requestInit);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        console.error('URL refresh failed:', {
          status: response.status,
          statusText: response.statusText,
          error: errorData
        });
        throw new Error(errorData.message || errorData.error || `Failed to refresh file URL: ${response.statusText}`);
      }

      const responseData = await response.json();
      console.log('URL refresh successful');
      return responseData.fileUrl;
    } catch (fetchError) {
      console.error('Network error during URL refresh:', fetchError);
      throw new Error('Network error while refreshing file URL. Please try again.');
    }
  } catch (error: unknown) {
    console.error('URL refresh error:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('An unknown error occurred while refreshing the URL');
  }
};
